import axios from "axios";
import axiosInstance from "./axios";
import type { ClientCustomizationsType } from "@/types/ClientTypes";
import { ResponseType } from "@/types/CommonTypes";
import { CreateGroupType, GroupType } from "@/types/GroupTypes";
import { UserWithProgressType } from "@/types/AuthTypes";
import { ScenarioType } from "@/types/ScenarioTypes";
import { SelectOption } from "@/types/SelectOptionTypes";
import { ReportType } from "@/types/ReportTypes";

export const groupsApi = {
  getAll: async (
    page: number,
    limit: number,
    searchTerm?: string,
    sortBy?: string,
    sortDirection?: "ascending" | "descending",
    signal?: AbortSignal,
  ): Promise<ResponseType<GroupType[]>> => {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append("page", page.toString());
      queryParams.append("limit", limit.toString());
      if (searchTerm && searchTerm !== "") {
        queryParams.append("search", searchTerm);
      }
      if (sortBy) {
        queryParams.append("sort_by", sortBy);
      }
      if (sortDirection) {
        queryParams.append(
          "sort_direction",
          sortDirection === "ascending" ? "ASC" : "DESC",
        );
      }

      const response = await axiosInstance.get(
        `/groups?${queryParams.toString()}`,
        { signal },
      );
      return response.data;
    } catch (error) {
      if (axios.isCancel(error)) throw error;
      throw error;
    }
  },
  getAllActive: async (): Promise<ResponseType<GroupType[]>> => {
    try {
      const response = await axiosInstance.get("/groups?active=true");
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  getById: async (
    groupId: number,
    params?: { include_client?: boolean },
  ): Promise<ResponseType<GroupType>> => {
    try {
      const response = await axiosInstance.get(`/groups/${groupId}`, {
        params,
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  create: async (
    group: CreateGroupType,
    userIds?: number[],
    scenarioIds?: number[],
  ): Promise<ResponseType<GroupType>> => {
    try {
      const response = await axiosInstance.post("/groups", {
        ...group,
        user_ids: userIds || [],
        scenario_ids: scenarioIds || [],
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  update: async (
    id: number,
    data: GroupType | CreateGroupType,
    userIds: number[],
    scenarioIds?: number[],
  ): Promise<ResponseType<GroupType>> => {
    try {
      const response = await axiosInstance.put(`/groups/${id}`, {
        ...data,
        user_ids: userIds,
        scenario_ids: scenarioIds || [],
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  /** Change PART of the group's customizations — merged on the server; `null` clears a key. */
  patchCustomizations: async (
    id: number,
    patch: Record<string, unknown>,
  ): Promise<ResponseType<{ customizations: ClientCustomizationsType; warnings: string[] }>> => {
    const response = await axiosInstance.patch(`/groups/${id}/customizations`, patch);
    return response.data;
  },
  updateActive: async (
    id: number,
    active: boolean,
  ): Promise<ResponseType<GroupType>> => {
    try {
      const response = await axiosInstance.patch(`/groups/${id}/active`, {
        active,
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  delete: async (id: number): Promise<ResponseType<void>> => {
    try {
      const response = await axiosInstance.delete(`/groups/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  assignUsers: async (
    groupId: number,
    userIds: number[],
  ): Promise<ResponseType<void>> => {
    try {
      const response = await axiosInstance.post(`/groups/${groupId}/users`, {
        userIds,
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  getGroupUsers: async (
    groupId: number,
    page?: number,
    limit?: number,
    searchTerm?: string,
    activeFilter?: number | null,
    ltiFilter?: number | null,
    sortBy?: string,
    sortDirection?: "ascending" | "descending",
    signal?: AbortSignal,
  ): Promise<ResponseType<UserWithProgressType[]>> => {
    try {
      const queryParams = new URLSearchParams();
      if (page) {
        queryParams.append("page", page.toString());
      }
      if (limit) {
        queryParams.append("limit", limit.toString());
      }
      if (searchTerm && searchTerm !== "") {
        queryParams.append("search", searchTerm);
      }
      if (activeFilter !== null && activeFilter !== undefined) {
        queryParams.append("active", activeFilter.toString());
      }
      // if (ltiFilter !== null && ltiFilter !== undefined) {
      //   queryParams.append("include_lti", ltiFilter.toString());
      // }
      if (sortBy) {
        queryParams.append("sort_by", sortBy);
      }
      if (sortDirection) {
        queryParams.append(
          "sort_direction",
          sortDirection === "ascending" ? "ASC" : "DESC",
        );
      }
      const response = await axiosInstance.get(
        `/groups/${groupId}/users?${queryParams.toString()}`,
        { signal },
      );
      return response.data;
    } catch (error) {
      if (axios.isCancel(error)) throw error;
      throw error;
    }
  },

  getEventProgress: async (
    page: number,
    limit: number,
    searchTerm?: string,
    sortBy?: string,
    sortDirection?: "ascending" | "descending",
    signal?: AbortSignal,
  ): Promise<ResponseType<GroupType[]>> => {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append("page", page.toString());
      queryParams.append("limit", limit.toString());
      if (searchTerm && searchTerm !== "") {
        queryParams.append("search", searchTerm);
      }
      if (sortBy) {
        queryParams.append("sort_by", sortBy);
      }
      if (sortDirection) {
        queryParams.append(
          "sort_direction",
          sortDirection === "ascending" ? "ASC" : "DESC",
        );
      }

      const response = await axiosInstance.get(
        `/groups/event-progress?${queryParams.toString()}`,
        { signal },
      );
      return response.data;
    } catch (error) {
      if (axios.isCancel(error)) throw error;
      throw error;
    }
  },
  removeUserFromGroup: async (
    groupId: number,
    userId: number,
  ): Promise<ResponseType<void>> => {
    try {
      // TODO : send userId array in body
      const response = await axiosInstance.delete(`/groups/${groupId}/users`, {
        data: {
          user_ids: [userId],
        },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  removeUsersFromGroup: async (
    groupId: number,
    userIds: number[],
  ): Promise<ResponseType<void>> => {
    try {
      const response = await axiosInstance.delete(`/groups/${groupId}/users`, {
        data: { user_ids: userIds },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  assignScenarios: async (
    groupId: number,
    scenarioIds: number[],
  ): Promise<ResponseType<void>> => {
    try {
      const response = await axiosInstance.post(
        `/groups/${groupId}/scenarios`,
        {
          scenarioIds,
        },
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  getGroupScenarios: async (
    groupId: number,
    page?: number,
    limit?: number,
  ): Promise<ResponseType<ScenarioType[]>> => {
    try {
      const queryParams = new URLSearchParams();
      if (page) {
        queryParams.append("page", page.toString());
      }
      if (limit) {
        queryParams.append("limit", limit.toString());
      }
      const response = await axiosInstance.get(
        `/groups/${groupId}/scenarios?${queryParams.toString()}`,
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  removeScenarioFromGroup: async (
    groupId: number,
    scenarioId: number,
  ): Promise<ResponseType<void>> => {
    try {
      const response = await axiosInstance.delete(
        `/groups/${groupId}/scenarios`,
        {
          data: {
            scenario_ids: [scenarioId],
          },
        },
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  removeScenariosFromGroup: async (
    groupId: number,
    scenarioIds: number[],
  ): Promise<ResponseType<void>> => {
    try {
      const response = await axiosInstance.delete(
        `/groups/${groupId}/scenarios`,
        {
          data: {
            scenario_ids: scenarioIds,
          },
        },
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  updateScenarioOrder: async (
    groupId: number,
    items: { scenario_id: number; position: number }[],
  ): Promise<ResponseType<{ success: boolean; message: string }>> => {
    try {
      const response = await axiosInstance.patch(
        `/groups/${groupId}/scenarios/order`,
        { items },
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  searchGroups: async (
    searchTerm: string,
    limit: number = 10,
  ): Promise<SelectOption[]> => {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append("search", searchTerm);
      queryParams.append("limit", limit.toString());
      queryParams.append("active", "true");

      const response = await axiosInstance.get(
        `/groups?${queryParams.toString()}`,
      );

      // Transform the response to match the expected format
      return response.data.data.map(
        (group: GroupType): SelectOption => ({
          id: group.id,
          name: group.name,
        }),
      );
    } catch (error) {
      throw error;
    }
  },
  getGroupsByClientId: async (
    clientId: number,
    page?: number,
    limit?: number,
  ): Promise<ResponseType<GroupType[]>> => {
    const queryParams = new URLSearchParams();
    if (page) {
      queryParams.append("page", page.toString());
    }
    if (limit) {
      queryParams.append("limit", limit.toString());
    }
    try {
      const response = await axiosInstance.get(
        `/groups/${clientId}/client?${queryParams.toString()}`,
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getLanguages: async (groupId: number): Promise<ResponseType<string[]>> => {
    try {
      const response = await axiosInstance.get(`/groups/${groupId}/languages`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  setLanguages: async (
    groupId: number,
    languageCodes: string[],
  ): Promise<ResponseType<void>> => {
    try {
      const response = await axiosInstance.post(
        `/groups/${groupId}/languages`,
        {
          languages: languageCodes,
        },
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  clearLanguages: async (groupId: number): Promise<ResponseType<void>> => {
    try {
      const response = await axiosInstance.delete(
        `/groups/${groupId}/languages`,
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getReports: async (groupId: number): Promise<ResponseType<ReportType[]>> => {
    try {
      const response = await axiosInstance.get(`/groups/${groupId}/reports`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  setReports: async (
    groupId: number,
    reportCodes: string[],
  ): Promise<ResponseType<unknown>> => {
    try {
      const response = await axiosInstance.post(`/groups/${groupId}/reports`, {
        reports: reportCodes,
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },
};
